import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Textarea } from "@/shared/ui/textarea.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { cn } from "@/shared/lib/cn.js";
import { DELAYS, STEP_TYPES, STEP_TYPE_IDS } from "../automation-constants.js";
import { useSaveStep } from "../use-automations.js";

/**
 * Editor de etapa da jornada — criar e editar usam o mesmo formulário.
 *
 * @param {{ automationId: string, step: object | null, open: boolean,
 *           onClose: () => void }} props
 */
export function StepEditorDialog({ automationId, step, open, onClose }) {
  const salvar = useSaveStep();
  const editando = Boolean(step?.id);

  // `key` no <Dialog> garante que o estado reinicia a cada etapa aberta,
  // sem precisar de um efeito sincronizando props com estado.
  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-w-lg">
        {open && (
          <Formulario
            key={step?.id ?? "nova"}
            automationId={automationId}
            step={step}
            editando={editando}
            salvar={salvar}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Formulario({ automationId, step, editando, salvar, onClose }) {
  const [form, setForm] = useState(() => ({
    delay: step?.delay ?? "Imediato",
    type: step?.type ?? "text",
    message: step?.message ?? "",
    attachmentName: step?.attachment?.name ?? "",
    attachmentUrl: step?.attachment?.url ?? "",
  }));

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  const precisaDeAnexo = form.type !== "text";

  function submeter(evento) {
    evento.preventDefault();
    salvar.mutate(
      {
        automationId,
        stepId: step?.id ?? null,
        delay: form.delay,
        type: form.type,
        message: form.message,
        attachmentName: form.attachmentName || null,
        attachmentUrl: form.attachmentUrl || null,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>{editando ? "Editar etapa" : "Nova etapa"}</DialogTitle>
        <DialogDescription>
          Use <code className="rounded bg-muted px-1">{"{nome}"}</code> e{" "}
          <code className="rounded bg-muted px-1">{"{procedimento}"}</code> para personalizar a
          mensagem.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Tipo de conteúdo</Label>
          <div className="flex flex-wrap gap-1.5">
            {STEP_TYPE_IDS.map((id) => {
              const meta = STEP_TYPES[id];
              const ativo = form.type === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => set("type")(id)}
                  aria-pressed={ativo}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
                    ativo
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  <meta.icon className="h-3.5 w-3.5" />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="step-delay">Quando enviar</Label>
          <Select value={form.delay} onValueChange={set("delay")}>
            <SelectTrigger id="step-delay">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELAYS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="step-message">Mensagem</Label>
          <Textarea
            id="step-message"
            rows={4}
            maxLength={2000}
            value={form.message}
            onChange={(e) => set("message")(e.target.value)}
            placeholder="Olá {nome}! 👋"
          />
        </div>

        {precisaDeAnexo && (
          <div className="grid gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="step-attachment-name">Nome do anexo</Label>
              <Input
                id="step-attachment-name"
                value={form.attachmentName}
                onChange={(e) => set("attachmentName")(e.target.value)}
                placeholder="guia-cuidados.pdf"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="step-attachment-url">URL</Label>
              <Input
                id="step-attachment-url"
                value={form.attachmentUrl}
                onChange={(e) => set("attachmentUrl")(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? <Spinner /> : null}
          Salvar etapa
        </Button>
      </DialogFooter>
    </form>
  );
}
